import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { environment } from '@env';

@Component({
  selector: 'app-reset',
  standalone: true,
  imports: [RouterLink, ReactiveFormsModule, CommonModule, TranslateModule],
  templateUrl: './reset.component.html',
  styleUrls: ['./reset.component.scss', '../../../../auth.scss'],

})
export class ResetPasswordComponent {
  baseUrl = environment.baseUrl;
  ResetForm: FormGroup;
  isPasswordVisible: boolean = false;
  isConfirmPasswordVisible: boolean = false;

  // ✅ password strength tracking
  passwordStrength = {
    lowercase: false,
    uppercase: false,
    number: false,
    specialChar: false,
    minLength: false,
  };

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private sharedService: SharedService,
    private pageService: PagesService
  ) {
    this.ResetForm = this.fb.group(
      {
        new_password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=[\\]{};\'"\\\\|,.<>/?]).{8,}$'
            ),
          ],
        ],
        confirmpassword: ['', [Validators.required]],
      },
      { validators: this.passwordMatchValidator }
    );


  }

  onPasswordInput(): void {
    const password = this.ResetForm.controls['new_password'].value || '';

    this.passwordStrength.lowercase = /[a-z]/.test(password);
    this.passwordStrength.uppercase = /[A-Z]/.test(password);
    this.passwordStrength.number = /\d/.test(password);
    this.passwordStrength.specialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    this.passwordStrength.minLength = password.length >= 8;
  }

  canSubmit(): boolean {
    return this.ResetForm.valid;
  }

  onSubmit() {
    this.sharedService.showLoader();
    if (this.canSubmit()) {
      const payload = {
        password: this.ResetForm.value.new_password,
        token: this.route.snapshot.paramMap.get('token'),
      };

      this.pageService.create('RESET_PASSWORD', payload).subscribe({
        next: (response: any) => {
          if (response.isSuccess) {
            this.router.navigate(['/auth/login']);
            this.sharedService.createNotification(
              'success',
              response.message || 'Password updated successfully'
            );
            this.sharedService.hideLoader();
          } else {
            this.sharedService.createNotification(
              'error',
              response.message || 'Something went wrong'
            );
            this.sharedService.hideLoader();
          }
        },
        error: (err: any) => {
          this.sharedService.hideLoader();
          const errorCode = err?.error?.code || err?.code;
          const errorMessage = err?.error?.message || err?.message;

          if (errorCode === 'ERROR_FATAL') {
            this.sharedService.createNotification(
              'error',
              errorMessage || 'Failed to reset password. Please try again.'
            );
          } else {
            this.sharedService.createNotification(
              'error',
              errorMessage || 'Failed to reset password. Please try again.'
            );
          }
        },
      });
    } else {
      this.ResetForm.markAllAsTouched();
      this.sharedService.createNotification(
        'error',
        'Please fix password requirements'
      );
      this.sharedService.hideLoader();
    }
  }

  togglePasswordType(type: string) {
    if (type === 'new_password') {
      this.isPasswordVisible = !this.isPasswordVisible;
    } else {
      this.isConfirmPasswordVisible = !this.isConfirmPasswordVisible;
    }
  }

  passwordMatchValidator(formGroup: FormGroup) {
    const password = formGroup.get('new_password')?.value;
    const confirmPassword = formGroup.get('confirmpassword')?.value;
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }
}
